import * as React from 'react';
import type { JSXElement } from '@fluentui/react-utilities';

import { DecoratorNode } from '@fluentui-copilot/text-editor';
import type { EditorConfig, LexicalNode, NodeKey, SerializedLexicalNode, Spread } from '@fluentui-copilot/text-editor';
import type { GhostTextProps } from '../../components/GhostText/GhostText.types';
import { GhostText } from '../../components/GhostText/GhostText';

export type SerializedGhostTextNode = Spread<
  {
    content: string;
    id: string;
    componentProps?: GhostTextProps;
    exposeText?: boolean;
  },
  SerializedLexicalNode
>;

export class GhostTextNode extends DecoratorNode<JSXElement | null> {
  __content: string;
  __id: string;
  __allowCommitting?: boolean;
  __componentProps?: GhostTextProps;
  __exposeText?: boolean;

  static clone(node: GhostTextNode): GhostTextNode {
    return new GhostTextNode(node.__id, node.__content, node.__exposeText, node.__componentProps, node.__key);
  }

  static getType(): 'ghosttext' {
    return 'ghosttext';
  }

  static importJSON(serializedNode: SerializedGhostTextNode): GhostTextNode {
    const node = $createGhostTextNode(
      serializedNode.id,
      serializedNode.content,
      serializedNode.exposeText,
      serializedNode.componentProps,
    );
    return node;
  }

  exportJSON(): SerializedGhostTextNode {
    return {
      type: 'ghosttext',
      id: this.__id,
      content: this.__content,
      componentProps: this.__componentProps,
      exposeText: this.__exposeText,
      version: 1,
    };
  }

  constructor(id: string, content: string, exposeText?: boolean, componentProps?: GhostTextProps, key?: NodeKey) {
    super(key);
    this.__id = id;
    this.__content = content;
    this.__exposeText = exposeText;
    this.__componentProps = componentProps;
  }

  isInline(): boolean {
    return true;
  }

  isIsolated(): boolean {
    return true;
  }

  getTextContent(): string {
    return this.__exposeText ? this.__content : '';
  }

  updateDOM(prevNode: unknown, dom: HTMLElement, config: EditorConfig): boolean {
    return false;
  }

  createDOM(config: EditorConfig): HTMLElement {
    // eslint-disable-next-line no-restricted-globals
    return document.createElement('span');
  }

  decorate(): JSXElement | null {
    return (
      <GhostText nodeKey={this.getKey()} {...this.__componentProps}>
        {this.__content}
      </GhostText>
    );
  }
}

export function $createGhostTextNode(
  id: string,
  content: string,
  exposeText?: boolean,
  componentProps?: GhostTextProps,
): GhostTextNode {
  return new GhostTextNode(id, content, exposeText, componentProps);
}

export function $isGhostTextNode(node: LexicalNode | null | undefined): node is GhostTextNode {
  return node instanceof GhostTextNode;
}
